import { useEffect, useRef, useState } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { IconWifiOff } from '@tabler/icons-react';
import { fetchIzmeneniya, type IzmenenieResursa } from './api';
import { mskDlyaPolya } from '../../lib/vremyaMsk';

/**
 * АКТУАЛИЗАЦИЯ БЕЗ F5 (`s713` по контракту `s712`, §7): раз в несколько секунд экран
 * спрашивает сервер «что изменилось после моей отметки» и перечитывает только то, что
 * изменилось и открыто. Источник — след действий: его пишут люди, платежи, синхронизация
 * УС и роботы, поэтому чужая правка, оплата и перенос из УС доезжают до открытого экрана сами.
 *
 * ⛔ ПОВТОРЫ НЕ ДЁРГАЮТ ЭКРАН: по ключу «вид:номер» хранится последний след, и ресурс
 * перечитывается, только если пришёл след больше.
 *
 * ⛔ СВЯЗЬ ПОТЕРЯНА — ГОВОРИМ: «Связь потеряна. Данные на HH:MM» (время последнего ответа
 * сервера), а не молча показываем застывшие данные. Вернулась — та же отметка довозит
 * пропущенное.
 *
 * ⛔ Прежний сервер (двери ленты нет) — компонент молчит и не опрашивает.
 */
const SEMEYSTVA: Record<string, (qc: QueryClient, id: number) => void> = {
  inquiry: (qc, id) => {
    void qc.invalidateQueries({ queryKey: ['obrashcheniya', 'razbor', id] });
    void qc.invalidateQueries({ queryKey: ['obrashcheniya', 'ochered'] });
    void qc.invalidateQueries({ queryKey: ['obrashcheniya', 'bar'] });
    void qc.invalidateQueries({ queryKey: ['doski', 'lidy'] });
  },
  booking: (qc, id) => {
    void qc.invalidateQueries({ queryKey: ['doski', 'zapisi'] });
    void qc.invalidateQueries({ queryKey: ['zapis', 'den'] });
    void qc.invalidateQueries({ queryKey: ['zapis', 'zakrytie', id] });
    void qc.invalidateQueries({ queryKey: ['zapis', 'karta', id] });
  },
  customer: (qc) => {
    void qc.invalidateQueries({ queryKey: ['okno-klientki'] });
  },
};

function perechitatVsyo(qc: QueryClient): void {
  for (const klyuch of [['obrashcheniya'], ['doski'], ['zapis'], ['okno-klientki']]) {
    void qc.invalidateQueries({ queryKey: klyuch });
  }
}

/* `s1027` G-16: «Данные на …» — по Москве, а не по часам компьютера. */
const vremya = (iso: string) => {
  const d = new Date(iso);

  return Number.isNaN(d.getTime()) ? '' : mskDlyaPolya(d).slice(11, 16);
};

/** Слово приходит с ответом ленты (словарь сервера); это — на случай, когда сервер его ещё не шлёт. */
const SLOVO_SVYAZI = 'Связь потеряна. Данные на {время}.';

export function LentaIzmeneniy() {
  const qc = useQueryClient();
  const [poteryana, setPoteryana] = useState<string | null>(null);
  const kursor = useRef<number | null>(null);
  const sledy = useRef(new Map<string, number>());
  const poslednee = useRef<string | null>(null);
  const slovo = useRef(SLOVO_SVYAZI);

  useEffect(() => {
    let zhiv = true;
    let taymer: number | undefined;
    let neudach = 0;

    const prinyat = (resursy: IzmenenieResursa[]) => {
      for (const r of resursy) {
        const k = `${r.tip}:${r.id}`;
        if ((sledy.current.get(k) ?? 0) >= r.sled) continue;
        sledy.current.set(k, r.sled);
        SEMEYSTVA[r.tip]?.(qc, r.id);
      }
      // Память следов не растёт без конца: держим последние, старые ресурсы перечитаются лишний раз.
      if (sledy.current.size > 2000) sledy.current = new Map([...sledy.current].slice(-1000));
    };

    const shag = async () => {
      let pauza = 5000;

      if (document.visibilityState === 'visible') {
        try {
          const pervyy = kursor.current === null;
          const otvet = await fetchIzmeneniya(kursor.current);
          if (!zhiv) return;

          kursor.current = otvet.kursor;
          poslednee.current = otvet.server_vremya;
          slovo.current = otvet.slova?.['aktualnost.svyaz_poteryana'] ?? slovo.current;
          pauza = Math.max(2, otvet.opros_sek ?? 5) * 1000;
          neudach = 0;
          setPoteryana(null);

          // Первый ответ — только отметка: экран и так свежий. Дальше «полное» значит «перечитай всё».
          if (otvet.polnoe && !pervyy) perechitatVsyo(qc);
          else if (!otvet.polnoe) prinyat(otvet.resursy);
        } catch (e: unknown) {
          if (!zhiv) return;
          const status = (e as { response?: { status?: number } })?.response?.status;

          // Двери нет или вход снят — не опрашиваем: это не «связь потеряна».
          if (status === 404 || status === 401 || status === 403) return;

          neudach += 1;
          // Один сорвавшийся запрос — ещё не потеря связи.
          if (neudach >= 2 && poslednee.current !== null) setPoteryana(slovo.current.replace('{время}', vremya(poslednee.current)));
        }
      }

      taymer = window.setTimeout(() => void shag(), pauza);
    };

    void shag();

    return () => {
      zhiv = false;
      window.clearTimeout(taymer);
    };
  }, [qc]);

  if (poteryana === null) return null;

  return (
    <div
      role="alert"
      data-testid="svyaz-poteryana"
      className="pointer-events-none fixed left-1/2 top-2 z-[1200] flex -translate-x-1/2 items-center gap-2 rounded-ops-pill bg-ops-danger px-4 py-2 text-ops-caption font-medium text-white shadow-lg"
    >
      <IconWifiOff size={18} /> {poteryana}
    </div>
  );
}
